import React, { useState, useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import PipelineBreadcrumb from './PipelineBreadcrumb';
import MeetingsTable      from './MeetingsTable';
import {
  PageWrapper,
  PageHeader,
  PageTitle,
  PageMeta,
  LastRefreshedLabel,
  RefreshButton,
  ErrorBanner,
} from './LiveMeetingsDashboard.styles';

const REFRESH_INTERVAL_MS = 30_000;

function formatRefreshedLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `Updated ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

function LiveMeetingsDashboard({ liveStore, adminStore, usersStore }) {
  const [selectedStages, setSelectedStages] = useState([]);
  const intervalRef = useRef(null);

  const currentUserName = usersStore?.currentUser?.userId || usersStore?.currentUser?.email || '';

  // Initial load
  useEffect(() => {
    liveStore.loadAll();
  }, [liveStore]);

  // Auto-refresh
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      liveStore.loadAll(true); // silent refresh — no loading spinner
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [liveStore]);

  const handleToggleStage = useCallback((stage) => {
    setSelectedStages(prev =>
      prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage]
    );
  }, []);

  const handleClearFilter = useCallback(() => {
    setSelectedStages([]);
  }, []);

  const handleManualRefresh = useCallback(() => {
    liveStore.loadAll();
  }, [liveStore]);

  const visibleItems = liveStore.filteredItems(selectedStages);

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Live Pipeline</PageTitle>
        <PageMeta>
          {liveStore.lastRefreshed && (
            <LastRefreshedLabel>{formatRefreshedLabel(liveStore.lastRefreshed)}</LastRefreshedLabel>
          )}
          <RefreshButton
            onClick={handleManualRefresh}
            disabled={liveStore.loading}
            title="Refresh now"
          >
            ↻ Refresh
          </RefreshButton>
        </PageMeta>
      </PageHeader>

      {liveStore.error && (
        <ErrorBanner>
          ⚠ Failed to load meetings — {liveStore.error}
        </ErrorBanner>
      )}

      <PipelineBreadcrumb
        stageCounts={liveStore.stageCounts}
        selectedStages={selectedStages}
        onToggleStage={handleToggleStage}
        onClearFilter={handleClearFilter}
      />

      <MeetingsTable
        items={visibleItems}
        loading={liveStore.loading}
        liveStore={liveStore}
        adminStore={adminStore}
        currentUserName={currentUserName}
      />
    </PageWrapper>
  );
}

export default observer(LiveMeetingsDashboard);
